import { MediaMatcher } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    SecurityContext,
} from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AppInitService } from 'src/app/shared/modules/core/services/app-initializer.service';
import {
    ConfigService,
    CurrentFacilityService,
    InitialConfigurationService,
    SummaryService,
} from 'src/app/shared/services';
import { BannersService } from 'src/app/shared/services/banners.service';
import { agiMaintenance, allianceFuelSurcharge } from 'src/app/shared/utils/notifications';
import { AllianceConfigurationService } from './services/alliance-config.service';

@Component({
    selector: 'app-root',
    templateUrl: './alliance.component.html',
    styleUrls: ['./alliance.component.scss'],
    standalone: false,
})
export class AllianceComponent implements OnInit, OnDestroy {
    private readonly appFavicon = this.document.getElementById('appFavicon');
    private unsubscribe$: Subject<void> = new Subject<void>();
    private readonly bannersService = inject(BannersService);
    private _mobileQueryListener: () => void;
    public mobileQuery: MediaQueryList;
    public bannerMessage: string;
    public bannerTitle: string;
    public isLoading$: Observable<boolean>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private titleService: Title,
        private currentFacilityService: CurrentFacilityService,
        private sanitizer: DomSanitizer,
        private initialConfigurationService: InitialConfigurationService,
        private allianceConfigurationService: AllianceConfigurationService,
        private summaryService: SummaryService,
        private configService: ConfigService,
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private appInitService: AppInitService
    ) {
        this.isLoading$ = this.appInitService.isLoading$;
        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    ngOnInit(): void {
        this.currentFacilityService.setFacilitySource('alliance');
        this.currentFacilityService.setInitialConfiguration({
            indexTitle: 'Alliance Ground International',
            urlFavIcon: 'assets/alliance/img/alliance-favicon.webp',
            mainLogoLightUrl: 'alliance/img/alliance-logo-light.webp',
            mainLogoDarkUrl: 'alliance/img/alliance-logo-dark.webp',
            urlAdministrative: 'https://sprintpay.cargosprint.com',
            name: 'AGI Cargo',
            email: 'payments@agi.aero',
            termsOfUseURL: 'https://agi.aero/termsofuse/',
            privacyAndPolicyURL: 'https://agi.aero/privacy-policy/',
            faqExternalURL: {
                pdfURL: 'https://firebasestorage.googleapis.com/v0/b/alliance-org-dce64.appspot.com/o/AGI.FAQ.V3.07.02.2024.pdf?alt=media&token=165c0c15-b8a0-40e5-8871-1a880b4b4acc',
            },
        });
        this.setFavicon();
        this.setTitle();
        this.subscribeInitialConfiguration();
        this.setGlobalConfig();
        this.setCustomerServiceInfo();
        this.displayAllianceBanners();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    setFavicon(): void {
        const favIcon = this.sanitizer.sanitize(SecurityContext.URL, this.currentFacilityService.getUrlFavIcon()) || '';

        this.renderer.setAttribute(this.appFavicon, 'href', favIcon);
    }

    setTitle(): void {
        const title = this.currentFacilityService.getIndexTitle();
        this.titleService.setTitle(title);
    }

    subscribeInitialConfiguration(): void {
        this.initialConfigurationService.setInitialConfiguration().pipe(takeUntil(this.unsubscribe$)).subscribe();
    }

    setGlobalConfig(): void {
        this.summaryService
            .getSearchType$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (searchType) => {
                    if (searchType) {
                        const chargesURL = this.allianceConfigurationService.getChargesURLBySearchType(searchType);

                        this.configService.setChargesURL(chargesURL);
                    }
                },
            });
    }

    subscribeWindowSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    private displayAllianceBanners(): void {
        this.bannersService.setBanners([allianceFuelSurcharge, agiMaintenance]);
    }

    setCustomerServiceInfo(): void {
        this.currentFacilityService.setCustomerServiceInfo({
            nameSection: 'AGI Customer Service',
            email: 'payments@agi.aero',
            phone: '(773) 462-9077',
            timeZone: '(Central Time)',
            operatingHours: [
                {
                    days: 'Monday to Friday',
                    schedule: '7 AM to 10:30 PM',
                },
                {
                    days: 'Saturday to Sunday',
                    schedule: '8 AM to 4:30 PM',
                },
            ],
        });
    }
}
